import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import type {JSX} from 'react'
import type {Block} from '@/services/kontent-ai/types'
import {Header} from '@/products/flexible-web-toolkit/components/block-header'
import {HeaderImage} from '@/products/flexible-web-toolkit/components/block-header/image'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {getColor} from '@/products/flexible-web-toolkit/colors'

export type SecondaryHeaderContentItem = IContentItem<{
	title: Elements.TextElement
	theme: Elements.MultipleChoiceElement
	content: Elements.TextElement
	image: Elements.AssetsElement
	brandColor: Elements.CustomElement
}>

export const SecondaryHeaderBlock: Block<SecondaryHeaderContentItem> = ({
	block,
	page,
}): JSX.Element => {
	// When the editor has explicitly chosen any theme (light OR dark) and has NOT
	// set a block-level brand color, the explicit theme styling should win over
	// any page-level brand color.  Otherwise the page color would silently
	// override the block's theme choice.
	const explicitTheme = block.elements.theme[0]?.codename
	// Skip page-level color whenever the editor has explicitly picked a theme
	// (light OR dark) and has not set a block-level brand color.
	// If no theme is explicitly chosen the page color is still a valid fallback.
	const skipPageColor = Boolean(explicitTheme) && !block.elements.brandColor

	// Background priority: block brand color > page-level brand color fallbacks > deprecated theme fallback
	// (page config is skipped when any explicit theme is set without a block-level brand color)
	const {bgClass, colorId} = colorWithFallback({
		blockColor: block.elements.brandColor,
		pageConfig: skipPageColor ? undefined : page.elements.config[0],
	})

	const hasCustomBackground = Boolean(bgClass)

	// If a resolved brand color is available, use registry textContrast; otherwise fall back to legacy theme field
	const isLightTheme = colorId
		? (getColor(colorId)?.textContrast ?? 'light') === 'dark'
		: (block.elements.theme[0]?.codename ?? 'light') === 'light'
	const textClassName =
		hasCustomBackground && !isLightTheme ? 'text-white' : undefined
	const contentClassName = textClassName ? `pt-6 ${textClassName}` : 'pt-6'

	return (
		<div data-kontent-element-codename="secondary_header">
			<Header
				bgColor={bgClass ?? undefined}
				light={isLightTheme}
				noTheme={hasCustomBackground}
			>
				{/* TODO: A11y lint: potential for more than one h1 on the page */}
				<h1 className={textClassName}>{block.elements.title}</h1>
				<p className={contentClassName}>{block.elements.content}</p>
				{block.elements.image[0] ? (
					// TODO: Use Next.js <Image /> if we're not already; also set height and width to prevent CLS
					<HeaderImage
						alt={block.elements.image[0].description || ''}
						src={block.elements.image[0].url}
					/>
				) : null}
			</Header>
		</div>
	)
}
